import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TopicService, Topic, TopicMessage, TopicMessageRequestDTO, MessageRole, TopicAttachment, Project, TopicStatus, MessageStatus, ProjectCommunicationStatus, Customer } from '../services/topic.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { QuillEditorComponent } from 'ngx-quill';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import Quill from 'quill';
import './quill-image-handler';
import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { EndpointFactory } from 'src/app/shared/services/endpoint.service';

@Component({
  selector: 'app-drafter-message',
  templateUrl: './drafter-message.component.html',
  styleUrls: ['./drafter-message.component.scss']
})
export class DrafterMessageComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('editor') editor!: QuillEditorComponent;
  @ViewChild('messageEditor') messageEditor!: QuillEditorComponent;
  @ViewChild('titleInput') titleInput!: ElementRef;

  messageForm: FormGroup;
  newTopicTitle: string = '';
  topics: Topic[] = [];
  selectedTopic?: Topic;
  messages: TopicMessage[] = [];
  currentUserName: string = '';
  submitted = false;
  success = false;
  creatingTopic = false;
  attachments: TopicAttachment[] = [];
  selectedFiles: File[] = [];
  showNotification = false;
  notificationMessage = '';
  unreadMessages: { [topicId: string]: number } = {};

  // Project selection properties
  projects: Project[] = [];
  selectedProject?: Project;
  showProjects = true;

  // Filter properties
  searchText: string = '';
  selectedStatus: string = 'all';
  sortOrder: string = 'newest';
  filteredTopics: Topic[] = [];
  showSidebar = false;

  // Admin customer selection properties
  isAdmin = false;
  showCustomerDropdown = false;
  customerSearchText = '';
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  selectedCustomer: Customer | null = null;
  isCustomerSpecificMessage = false;

  // Message editing properties
  isEditingMessage: string | null = null;
  editingMessageContent: string = '';
  isSelectingCustomer: string | null = null;
  editingAttachments: TopicAttachment[] = [];
  editingImages: string[] = [];
  isEditingTitle = false;
  editingTitle = '';
  hasCyrillicError = false;
  cyrillicErrorMessage = 'Cyrillic characters are not allowed';
  hasTitleCyrillicError = false;
  hasMessageCyrillicError = false;
  hasEditingMessageCyrillicError = false;
  hasNewTopicCyrillicError = false;
  isDragging = false;
  loggedInUserId: string = '';
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ],
    keyboard: {
      bindings: {
        enter: {
          key: 13,
          handler: (range: any, context: any) => {
            // If shift is pressed, insert a new line
            if (context.shiftKey) {
              return true; // Let Quill handle it normally (new line)
            }

            // If we're editing a message, save the edit
            if (this.isEditingMessage) {
              const message = this.messages.find(m => m.id === this.isEditingMessage);
              if (message) {
                this.saveMessageEdit(message);
                return false; // Prevent default behavior
              }
            }

            // Otherwise, submit the new message form
            this.onSubmit();
            return false; // Prevent default behavior
          }
        }
      }
    },
    imageHandler: {
      upload: (file: File) => {
        return new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('container', 'test');

          const token = this.authService.getAuthBearerToken();
          const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

          this.http.post<any>(this.endpointFactory.UploadImage, formData, { headers }).subscribe({
            next: (response) => {
              if (response.isSuccess) {
                resolve(response.result.url);
              } else {
                reject(new Error('Failed to upload image'));
              }
            },
            error: (error) => {
              console.error('Image upload error:', error);
              reject(error);
            }
          });
        });
      }
    }
  };

  isEditingStatus = false;

  isSending = false;

  MessageRole = MessageRole; // Expose MessageRole to template

  unreadMessagesCountByTopic: { [topicId: string]: number } = {};

  getUnreadMessages = false;
  private messageCheckInterval: any;
  private initialCheckTimeout: any;

  isCustomerAdmin = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private topicService: TopicService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private endpointFactory: EndpointFactory
  ) {
    this.messageForm = this.fb.group({
      message: ['']
    });

    // Configure Quill image format
    const Image = Quill.import('formats/image');
    Image.sanitize = (url: string) => url;
    Image.className = 'editor-image';
    Quill.register(Image, true);

    // Configure Quill to allow text selection
    const Block = Quill.import('blots/block');
    Block.tagName = 'DIV';
    Block.className = 'selectable-text';
    Quill.register(Block, true);
  }

  ngOnInit() {
    this.currentUserName = this.authService.getUserName();
    this.loadProjects();
    this.checkAdminStatus();
    this.loadCustomers();
    this.currentUserName = this.authService.getUserName();

    // Get current user ID
    this.authService.getCurrentUser().subscribe(user => {
      this.loggedInUserId = user.id;
    });

    this.isCustomerAdmin = this.authService.hasRoles('CUSTOMER_ADMIN', 'TEST_USER');

    // Only check for new messages if the user is logged in
    if (this.authService.isLoggedIn()) {
      // timeout on 5 seconds before the first request
      this.initialCheckTimeout = setTimeout(() => {
        this.checkAndUpdateNewMessages();

        // If the user is on the messages page — start the interval
      if (this.router.url.includes('/user/messages')) {
        this.messageCheckInterval = setInterval(() => {
          this.checkAndUpdateNewMessages();
          }, 30000); // 30 seconds
        }
      }, 5000); // 5 seconds
    }
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  shouldScrollToBottom = false;

  private scrollToBottom(): void {
    if (!this.shouldScrollToBottom) return;

    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
      this.shouldScrollToBottom = false;
    } catch (err) {}
  }

  loadProjects() {
    this.topicService.getAllProjects().subscribe(data => {
      // Filter out archived projects
      this.projects = data.filter(project => project.status !== ProjectCommunicationStatus.ARCHIVED);
    });
  }

  selectProject(project: Project) {
    console.log('Project selected:', project);
    this.selectedProject = project;
    this.showProjects = false;
    this.loadTopics();
  }

  backToProjects() {
    this.showProjects = true;
    this.selectedProject = undefined;
    this.selectedTopic = undefined;
    this.messages = [];
  }

  loadTopics() {
    if (!this.selectedProject) return;

    console.log('Loading topics for project:', this.selectedProject.id);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    this.topicService.getTopicsByProjectId(this.selectedProject.id).subscribe({
      next: (data) => {
        console.log('Received topics:', data);
        // Handle both empty array and 204 No Content response
        let topics = Array.isArray(data) ? data : [];

        // Filter topics based on user role
        if (this.isCustomerAdmin) {
          topics = topics.filter(topic =>
            topic.messages?.some(msg => msg.allowedRoles?.includes(MessageRole.CUSTOMER_ADMIN))
          );
        }

        this.topics = topics;
        this.applyFilters(); // Apply filters when topics are loaded
        console.log('After applying filters, filteredTopics:', this.filteredTopics);
        // Auto-select the first topic if none is selected
        if (!this.selectedTopic && this.topics.length) {
          this.selectTopic(this.topics[0]);
        }
        // Check and update project status after loading topics
        this.checkAndUpdateProjectStatus();
      },
      error: (error) => {
        console.error('Failed to load topics:', error);
        this.topics = [];
        this.filteredTopics = [];
      }
    });
  }

  selectTopic(topic: Topic) {
    this.selectedTopic = topic;
    let messages = Array.isArray(topic.messages) ? topic.messages : [];

    // Filter messages based on user role
    if (this.isCustomerAdmin) {
      messages = messages.filter(msg =>
        msg.allowedRoles?.includes(MessageRole.CUSTOMER_ADMIN)
      );
    }

    this.messages = messages;
    this.shouldScrollToBottom = true;
    this.success = false;

    // Clear unread count after 5 seconds
    setTimeout(() => {
      this.unreadMessagesCountByTopic[topic.id] = 0;
      this.clearUnreadCount(topic.id);
    }, 5000);

    // Mark messages as visited when selecting a topic
    if (this.selectedProject) {
      this.topicService.markTopicMessagesAsVisited(this.selectedProject.id, topic.id).subscribe({
        next: () => {
          // Update the unread messages count after marking as visited
          this.checkAndUpdateNewMessages();
        }
      });
    }

    this.loadMessages(topic.id);
  }

  loadMessages(topicId: string) {
    if (!this.selectedProject) return;

    this.topicService.getTopicMessages(this.selectedProject.id, topicId).subscribe({
      next: (topic) => {
        // Update the topic in the topics array
        const index = this.topics.findIndex(t => t.id === topicId);
        if (index !== -1) {
          this.topics[index] = topic;
        }

        // Update selected topic and messages
        if (this.selectedTopic?.id === topicId) {
          this.selectedTopic = topic;
          // Filter messages based on user role
          let filteredMessages = Array.isArray(topic.messages) ? topic.messages : [];
          if (this.isCustomerAdmin) {
            filteredMessages = filteredMessages.filter(msg =>
              msg.allowedRoles?.includes(MessageRole.CUSTOMER_ADMIN)
            );
          }
          this.messages = filteredMessages;
        }

        setTimeout(() => this.scrollToBottom(), 0);
      },
      error: (error) => {
        console.error('Failed to load messages:', error);
      }
    });
  }

  createNewTopic() {
    if (!this.newTopicTitle.trim()) return;

    // Check for Cyrillic characters
    if (this.containsCyrillic(this.newTopicTitle)) {
      this.hasNewTopicCyrillicError = true;
      return;
    }

    // Get current user's email from auth service
    this.authService.getCurrentUser().subscribe(user => {
      if (!user.email) {
        console.error('User email is not available');
        return;
      }

      const members = [user.email]; // Add more members as needed

      if (!this.selectedProject) {
        console.error('No project selected');
        return;
      }

      this.topicService.createTopic(this.newTopicTitle.trim(), members, this.selectedProject.id).subscribe(newTopic => {
        this.loadTopics();
        this.selectTopic(newTopic);
        this.newTopicTitle = '';
        this.creatingTopic = false;
        this.hasNewTopicCyrillicError = false;
      });
    });
  }

  checkNewTopicCyrillic() {
    this.hasNewTopicCyrillicError = this.containsCyrillic(this.newTopicTitle);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.selectedTopic) {
      return;
    }

    Array.from(input.files).forEach(file => {
      // Show loading state
      this.showNotification = true;
      this.notificationMessage = `Uploading ${file.name}...`;

      this.topicService.uploadAttachment(file, this.selectedTopic!.id).subscribe({
        next: (attachment) => {
          if (this.isEditingMessage) {
            this.editingAttachments.push(attachment);
          } else {
            this.attachments.push(attachment);
            this.selectedFiles.push(file);
          }
          // Show success message
          this.showNotification = true;
          this.notificationMessage = `Successfully uploaded ${file.name}`;
        },
        error: (error) => {
          console.error('Failed to upload file:', error);
          // Show error notification
          this.showNotification = true;
          this.notificationMessage = `Failed to upload ${file.name}: ${error.message}`;
        }
      });
    });

    // Clear the input
    input.value = '';
  }

  removeAttachment(attachment: TopicAttachment) {
    this.attachments = this.attachments.filter(a => a.url !== attachment.url);
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== attachment.fileName);
    this.topicService.deleteFileByUrl(attachment.url).subscribe({
      next: () => {
        console.log('File deleted successfully');
      },
      error: (err) => {
        console.error('Failed to delete file:', err);
      }
    });
  }

  downloadAttachment(attachment: TopicAttachment) {
    this.topicService.downloadAttachment(attachment);
  }

  onSubmit() {
    if (this.isSending) return;
    this.submitted = true;
    this.success = false;

    if (!this.selectedTopic) return;

    const messageContent = this.editor.quillEditor.root.innerHTML;
    // Check if there's at least 2 characters of text content
    const hasContent = messageContent.trim().replace(/<[^>]*>/g, '').length >= 2;
    const hasAttachments = this.attachments.length > 0;
    const hasImages = this.editor.quillEditor.getContents().ops?.some(op => op.insert?.image) || false;

    // Check if there's any content (text, images, or attachments)
    if (!hasContent && !hasAttachments && !hasImages) {
      return;
    }

    // Check for Cyrillic characters in the message content
    if (hasContent && this.containsCyrillic(messageContent)) {
      this.hasMessageCyrillicError = true;
      return;
    }

    this.isSending = true;

    setTimeout(() => {
      this.isSending = false;
    }, 2000);

    this.authService.getCurrentUser().subscribe(user => {
      if (!user.id || !this.selectedTopic) {
        console.error('User ID or selected topic is not available');
        return;
      }

      // Determine allowed roles based on user's role
      let allowedRoles: MessageRole[];
      let messageRole: MessageRole;

      if (this.isCustomerAdmin) {
        // If user is CUSTOMER_ADMIN or TEST_USER, allow DRAFTER, CUSTOMER_ADMIN, and ADMIN roles
        allowedRoles = [MessageRole.DRAFTER, MessageRole.CUSTOMER_ADMIN, MessageRole.ADMIN];
        messageRole = MessageRole.CUSTOMER_ADMIN;
      } else {
        // For other roles (ADMIN, DRAFTER), allow DRAFTER and ADMIN roles
        allowedRoles = [MessageRole.DRAFTER, MessageRole.ADMIN];
        messageRole = this.authService.isAdmin() ? MessageRole.ADMIN : MessageRole.DRAFTER;
      }

      const messageDTO: TopicMessageRequestDTO = {
        topicStatus: this.selectedTopic.status,
        authorId: this.currentUserName,
        authorName: this.currentUserName,
        allowedRoles: allowedRoles,
        members: this.isCustomerSpecificMessage && this.selectedCustomer
          ? [this.selectedCustomer.email]
          : this.selectedTopic.members,
        content: messageContent,
        imageUrls: [],
        attachments: this.attachments,
        role: messageRole,
        id: this.selectedTopic.id,
        projectId: this.selectedProject!.id
      };

      this.topicService.sendMessage(messageDTO).subscribe(() => {
        this.success = true;
        this.messageForm.controls['message'].setValue('');
        this.attachments = [];
        this.selectedFiles = [];
        this.isCustomerSpecificMessage = false;
        this.hasMessageCyrillicError = false;

        const Delta = Quill.import('delta');
        this.editor.quillEditor.setContents(new Delta());

        this.submitted = false;
        this.shouldScrollToBottom = true;
        this.loadMessages(this.selectedTopic!.id);
      });
    });
  }

  sanitizeHtml(html: string): SafeHtml {
    // Create a temporary div to manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all images and add custom styles
    const images = tempDiv.getElementsByTagName('img');
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      img.style.maxWidth = '200px';
      img.style.maxHeight = '150px';
      img.style.objectFit = 'contain';
      img.style.borderRadius = '4px';
      img.style.margin = '8px 0';
      img.style.cursor = 'pointer';
      img.style.transition = 'transform 0.2s ease';
      img.classList.add('message-image');
    }

    // Add selectable-text class to all text elements
    const elements = tempDiv.getElementsByTagName('*');
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      if (element.tagName !== 'IMG') {
        element.classList.add('selectable-text');
      }
    }

    return this.sanitizer.bypassSecurityTrustHtml(tempDiv.innerHTML);
  }

  get f() {
    return this.messageForm.controls;
  }

  getFileTypeIcon(fileType: string): string {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) {
      return 'pdf';
    } else if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xls') || type.includes('xlsx')) {
      return 'excel';
    } else if (type.includes('word') || type.includes('doc') || type.includes('docx')) {
      return 'word';
    } else if (type.includes('image')) {
      return 'image';
    } else if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
      return 'archive';
    } else {
      return 'file';
    }
  }

  getFileTypeLabel(fileType: string): string {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) {
      return 'PDF Document';
    } else if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xls') || type.includes('xlsx')) {
      return 'Excel Spreadsheet';
    } else if (type.includes('word') || type.includes('doc') || type.includes('docx')) {
      return 'Word Document';
    } else if (type.includes('image')) {
      return 'Image';
    } else if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
      return 'Archive';
    } else {
      return 'File';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  incrementUnreadCount(topicId: string) {
    this.unreadMessages[topicId] = (this.unreadMessages[topicId] || 0) + 1;
  }

  clearUnreadCount(topicId: string) {
    this.unreadMessages[topicId] = 0;
  }

  handleNotificationAction(action: 'view' | 'dismiss') {
    if (action === 'view') {
      const topic = this.topics.find(t => t.id === this.selectedTopic?.id);
      if (topic) {
        this.selectTopic(topic);
        this.clearUnreadCount(topic.id);
      }
    }
    this.showNotification = false;
  }

  // Filter methods
  onSearchInput(value: string) {
    this.searchText = value;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.topics];

    // Apply search filter
    if (this.searchText) {
      const searchTerm = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(topic =>
        topic.title.toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (this.selectedStatus !== 'all') {
      filtered = filtered.filter(topic => topic.status === this.selectedStatus);
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    this.filteredTopics = filtered;
  }

  clearFilters() {
    this.searchText = '';
    this.selectedStatus = 'all';
    this.sortOrder = 'newest';
    this.applyFilters();
  }

  // Add method to check if any filters are active
  hasActiveFilters(): boolean {
    return this.searchText !== '' ||
           this.selectedStatus !== 'all' ||
           this.sortOrder !== 'newest';
  }

  toggleSidebar() {
    this.showSidebar = !this.showSidebar;
  }

  isFilterActive(): boolean {
    return this.searchText !== '' ||
           this.selectedStatus !== 'all' ||
           this.sortOrder !== 'newest';
  }

  private checkAdminStatus() {
    this.isAdmin = this.authService.isAdmin();
  }

  private loadCustomers() {
    if (!this.selectedProject) return;

    // Get client ID from project
    const clientId = this.selectedProject.clientId;
    if (!clientId) {
      console.error('No client ID found for project');
      return;
    }

    // Fetch customers for the client ID
    this.topicService.getCustomersByClientId(clientId).subscribe({
      next: (customers) => {
        this.customers = customers;
        this.filteredCustomers = [...this.customers];
        console.log('Loaded customers:', this.customers);
      },
      error: (error) => {
        console.error('Failed to load customers:', error);
        this.customers = [];
        this.filteredCustomers = [];
      }
    });
  }

  getCustomerName(email: string): string {
    const customer = this.customers.find(c => c.email === email);
    return customer ? customer.name : email;
  }

  toggleStatusEdit() {
    this.isEditingStatus = !this.isEditingStatus;
  }

  onStatusChange() {
    if (!this.selectedTopic || !this.selectedProject) return;

    this.topicService.updateTopicStatus(this.selectedProject.id, this.selectedTopic.id, this.selectedTopic.status)
      .subscribe({
        next: () => {
          this.isEditingStatus = false;
          // Check and update project status after topic status changes
          this.checkAndUpdateProjectStatus();
        },
        error: (error) => {
          console.error('Failed to update status:', error);
          // Revert the status change if the API call fails
          this.selectedTopic!.status = this.selectedTopic!.status;
        }
      });
  }

  toggleMessageEdit(message: TopicMessage) {
    if (this.isEditingMessage === message.id) {
      this.cancelMessageEdit();
    } else {
      this.isEditingMessage = message.id;
      this.editingMessageContent = message.content;
      this.editingAttachments = [...(message.attachments || [])];
      this.editingImages = [...(message.imageUrls || [])];
      this.isSelectingCustomer = null;
      this.hasEditingMessageCyrillicError = false;
    }
  }

  cancelMessageEdit() {
    this.isEditingMessage = null;
    this.editingMessageContent = '';
    this.editingAttachments = [];
    this.editingImages = [];
    this.hasEditingMessageCyrillicError = false;
  }

  saveMessageEdit(message: TopicMessage) {
    if (this.containsCyrillic(this.editingMessageContent)) {
      this.hasEditingMessageCyrillicError = true;
      return;
    }

    if (!this.selectedTopic || !this.selectedProject) {
      console.error('No topic or project selected');
      return;
    }

    // Check if there's at least 2 characters of text content
    const hasContent = this.editingMessageContent.trim().replace(/<[^>]*>/g, '').length >= 2;
    const hasAttachments = this.editingAttachments.length > 0;
    const hasImages = this.editingImages.length > 0;

    // Check if there's any content (text, images, or attachments)
    if (!hasContent && !hasAttachments && !hasImages) {
      return;
    }

    const updatedMessage: TopicMessage = {
      ...message,
      content: this.editingMessageContent,
      attachments: this.editingAttachments,
      imageUrls: this.editingImages,
      discussionId: this.selectedTopic.id
    };

    this.topicService.updateMessage(updatedMessage, this.selectedProject.id).subscribe(
      (response) => {
        // Update the message in the local messages array
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages[index] = updatedMessage;
        }

        // Reset editing state
        this.isEditingMessage = null;
        this.editingMessageContent = '';
        this.editingAttachments = [];
        this.editingImages = [];
        this.hasMessageCyrillicError = false;

        // Refresh topics to show updated message
        this.loadMessages(this.selectedTopic!.id);

        // Trigger change detection
        this.shouldScrollToBottom = true;
      },
      error => {
        console.error('Error updating message:', error);
      }
    );
  }

  deleteEditingAttachment(attachment: TopicAttachment) {
    this.topicService.deleteFileByUrl(attachment.url).subscribe({
      next: () => {
        this.editingAttachments = this.editingAttachments.filter(a => a.url !== attachment.url);
      },
      error: (err) => {
        console.error('Failed to delete file:', err);
      }
    });
  }

  deleteEditingImage(imageUrl: string) {
    this.editingImages = this.editingImages.filter(url => url !== imageUrl);
  }

  toggleCustomerSelection(message: TopicMessage) {
    if (this.isSelectingCustomer === message.id) {
      this.isSelectingCustomer = null;
    } else {
      this.isSelectingCustomer = message.id;
      this.isEditingMessage = null;
      this.loadCustomers();
    }
  }

  selectCustomerForTopic(customer: Customer) {
    if (!this.selectedTopic || !this.selectedProject) {
      console.error('No topic or project selected');
      return;
    }

    const selectedTopic = this.selectedTopic;
    const selectedProject = this.selectedProject;

    // Get the message being edited
    const message = this.messages.find(m => m.id === this.isSelectingCustomer);
    if (!message) {
      console.error('No message found for customer selection');
      return;
    }

    // Update the message's allowedRoles and members
    const updatedMessage: TopicMessage = {
      ...message,
      allowedRoles: [...(message.allowedRoles || []), MessageRole.CUSTOMER_ADMIN],
      members: message.members?.includes(customer.email)
        ? message.members.filter(email => email !== customer.email)
        : [...(message.members || []), customer.email],
      discussionId: selectedTopic.id
    };

    // Update the message through the service
    this.topicService.updateMessage(updatedMessage, selectedProject.id).subscribe(
      () => {
        // Update the local message
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages[index] = updatedMessage;
        }

        // Update topic status to SENT_TO_CUSTOMER
        selectedTopic.status = TopicStatus.SENT_TO_CUSTOMER;
        this.topicService.updateTopicStatus(selectedProject.id, selectedTopic.id, TopicStatus.SENT_TO_CUSTOMER)
          .subscribe({
            next: () => {
              // Check and update project status after topic status changes
              this.checkAndUpdateProjectStatus();
            },
            error: (error) => {
              console.error('Failed to update topic status:', error);
            }
          });

        this.isSelectingCustomer = null;
      },
      error => {
        console.error('Failed to update message:', error);
      }
    );
  }

  toggleTitleEdit() {
    this.isEditingTitle = !this.isEditingTitle;
    if (this.isEditingTitle && this.selectedTopic) {
      this.editingTitle = this.selectedTopic.title;
      this.hasTitleCyrillicError = false;
      // Focus the input after the view updates
      setTimeout(() => {
        this.titleInput?.nativeElement?.focus();
      });
    }
  }

  saveTitleEdit() {
    if (!this.editingTitle.trim() || !this.selectedTopic || !this.selectedProject) return;

    // Check for Cyrillic characters
    if (this.containsCyrillic(this.editingTitle)) {
      this.hasTitleCyrillicError = true;
      return;
    }

    const projectId = this.selectedProject.id;
    const topicId = this.selectedTopic.id;
    const newTitle = this.editingTitle.trim();

    // Update the topic title
    this.topicService.updateTopicTitle(projectId, topicId, newTitle).subscribe({
      next: () => {
        if (this.selectedTopic) {
          this.selectedTopic.title = newTitle;
        }
        this.isEditingTitle = false;
        this.hasTitleCyrillicError = false;
      },
      error: (error) => {
        console.error('Failed to update topic title:', error);
        // Revert the change if the API call fails
        if (this.selectedTopic) {
          this.editingTitle = this.selectedTopic.title;
        }
      }
    });
  }

  cancelTitleEdit() {
    this.isEditingTitle = false;
    this.editingTitle = '';
  }

  private containsCyrillic(text: string): boolean {
    // Cyrillic Unicode range: U+0400 to U+04FF
    return /[\u0400-\u04FF]/.test(text);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files?.length || !this.selectedTopic) {
      return;
    }

    Array.from(files).forEach(file => {
      // Show loading state
      this.showNotification = true;
      this.notificationMessage = `Uploading ${file.name}...`;

      this.topicService.uploadAttachment(file, this.selectedTopic!.id).subscribe({
        next: (attachment) => {
          if (this.isEditingMessage) {
            this.editingAttachments.push(attachment);
          } else {
            this.attachments.push(attachment);
            this.selectedFiles.push(file);
          }
          // Show success message
          this.showNotification = true;
          this.notificationMessage = `Successfully uploaded ${file.name}`;
        },
        error: (error) => {
          console.error('Failed to upload file:', error);
          // Show error notification
          this.showNotification = true;
          this.notificationMessage = `Failed to upload ${file.name}: ${error.message}`;
        }
      });
    });
  }

  isCustomerSelectedForMessage(customer: Customer, messageId: string): boolean {
    const message = this.messages.find(m => m.id === messageId);
    if (!message) return false;

    return (
      message.members?.includes(customer.email) &&
      message.allowedRoles?.includes(MessageRole.CUSTOMER_ADMIN)
    );
  }

  trackByEmail(index: number, customer: Customer): string {
    return customer.email;
  }

  isMyMessage(msg: TopicMessage): boolean {
    return msg.authorId === this.currentUserName;
  }

  isCustomerAdminMessage(msg: any): boolean {
    return msg.allowedRoles?.includes('CustomerAdmin');
  }

  copyMessage(message: TopicMessage): void {
    // Get the message content without HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = message.content;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Add attachments info if any
    let fullContent = textContent;
    if (message.attachments?.length) {
      fullContent += '\n\nAttachments:\n';
      message.attachments.forEach(attachment => {
        fullContent += `- ${attachment.fileName}\n`;
      });
    }

    // Copy to clipboard
    navigator.clipboard.writeText(fullContent).then(() => {
      // Show success notification
      this.showNotification = true;
      this.notificationMessage = 'Message copied to clipboard';
      setTimeout(() => {
        this.showNotification = false;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy message:', err);
      this.showNotification = true;
      this.notificationMessage = 'Failed to copy message';
      setTimeout(() => {
        this.showNotification = false;
      }, 2000);
    });
  }

  ngOnDestroy() {
    if (this.messageCheckInterval) {
      clearInterval(this.messageCheckInterval);
    }
    if (this.initialCheckTimeout) {
      clearTimeout(this.initialCheckTimeout);
    }
  }

  private checkAndUpdateNewMessages(): void {
    this.topicService.checkNewMessages().subscribe({
      next: (result) => {
        this.unreadMessagesCountByTopic = {};

        Object.entries(result).forEach(([topicId, messages]) => {
          const unreadCount = messages.filter(msg => !msg.readBy || msg.readBy.length === 0).length;
          if (unreadCount > 0) {
            this.unreadMessagesCountByTopic[topicId] = unreadCount;
          }

          // If this is the currently selected topic, update its messages
          if (this.selectedTopic && this.selectedTopic.id === topicId) {
            this.loadMessages(topicId);
          }
        });

        this.getUnreadMessages = Object.keys(this.unreadMessagesCountByTopic).length > 0;
        console.log('Unread messages by topic:', this.unreadMessagesCountByTopic);
        console.log('Has unread messages:', this.getUnreadMessages);
      },
      error: (err) => {
        console.error('Error fetching new messages:', err);
        this.getUnreadMessages = false;
        this.unreadMessagesCountByTopic = {};
      }
    });
  }

  getTotalUnreadMessages(): number {
    return Object.values(this.unreadMessagesCountByTopic).reduce((sum, count) => sum + count, 0);
  }

  private checkAndUpdateProjectStatus(): void {
    if (!this.selectedProject) return;

    // If there are no topics, keep the current status
    if (!this.topics.length) return;

    // Check if all topics are resolved
    const allTopicsResolved = this.topics.every(topic => topic.status === TopicStatus.RESOLVED);

    // If all topics are resolved, set project to COMPLETED
    if (allTopicsResolved) {
      const url = this.endpointFactory.UpdateProjectStatus.replace(':id', this.selectedProject.id);
      const token = this.authService.getAuthBearerToken();
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });

      // Send status as JSON string
      const body = JSON.stringify(ProjectCommunicationStatus.COMPLETED);

      this.http.patch<any>(url, body, { headers }).subscribe({
        next: (response) => {
          if (response.isSuccess && this.selectedProject) {
            this.selectedProject.status = ProjectCommunicationStatus.COMPLETED;
          }
        },
        error: (error) => {
          console.error('Failed to update project status:', error);
        }
      });
    }
    // If there are topics but not all are resolved, set to IN_PROGRESS
    else if (this.selectedProject.status === ProjectCommunicationStatus.NEW ||
             this.selectedProject.status === ProjectCommunicationStatus.COMPLETED) {
      const url = this.endpointFactory.UpdateProjectStatus.replace(':id', this.selectedProject.id);
      const token = this.authService.getAuthBearerToken();
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });

      // Send status as JSON string
      const body = JSON.stringify(ProjectCommunicationStatus.IN_PROGRESS);

      this.http.patch<any>(url, body, { headers }).subscribe({
        next: (response) => {
          if (response.isSuccess && this.selectedProject) {
            this.selectedProject.status = ProjectCommunicationStatus.IN_PROGRESS;
          }
        },
        error: (error) => {
          console.error('Failed to update project status:', error);
        }
      });
    }
  }

  // Add these event handler methods for the template
  onProjectSelected(project: Project) {
    this.selectedProject = project;
    this.showProjects = false;

    // Load topics and then select the most recent one
    this.topicService.getTopicsByProjectId(project.id).subscribe({
      next: (topics) => {
        this.topics = topics;

        // Find the topic with the most recent message
        let mostRecentTopic: Topic | undefined;
        let mostRecentDate = new Date(0); // Initialize with earliest possible date

        topics.forEach(topic => {
          if (topic.messages && topic.messages.length > 0) {
            // Find the most recent message in this topic
            const mostRecentMessage = topic.messages.reduce((latest, current) => {
              const latestDate = new Date(latest.created);
              const currentDate = new Date(current.created);
              return currentDate > latestDate ? current : latest;
            });

            const messageDate = new Date(mostRecentMessage.created);
            if (messageDate > mostRecentDate) {
              mostRecentDate = messageDate;
              mostRecentTopic = topic;
            }
          }
        });

        // If we found a topic with messages, select it
        if (mostRecentTopic) {
          this.selectTopic(mostRecentTopic);
        } else if (topics.length > 0) {
          // If no messages but topics exist, select the first topic
          this.selectTopic(topics[0]);
        }

        this.applyFilters();
      },
      error: (error) => {
        console.error('Failed to load topics:', error);
        this.topics = [];
        this.filteredTopics = [];
      }
    });
  }

  onProjectArchived(project: Project) {
    // Optionally handle project archived event
    if (this.selectedProject && this.selectedProject.id === project.id) {
      this.backToProjects();
    }
  }

  onTopicSelected(topic: Topic) {
    this.selectTopic(topic);
  }
}
